<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { DsText } from '@coloplast/design-system';
import VSelect from 'vue-select';

const props = defineProps({
	placeholder: String,
	id: String,
	fieldName: String,
	name: String,
	errors: Object,
	value: String,
	hasError: Function,
	cannotFindAddressLabel: String,
	maxSuggestions: Number,
});
const emit = defineEmits(['validateField', 'onBlur']);
const store = useStore();
const language = store.getters['getCulture'];
const suggestions = computed(() => store.getters['getAddressSuggestions']);
const isValid = ref(true);


async function onInput(event) {
	try {
		await store.dispatch('updatePostalCode', event.target.value);
		await search(event.target.value);
	} catch (err) {
		console.error(err);
	}
}

async function search(query) {
	emit('validateField', query);

	if (!query || query.length < 3) {
		isValid.value = false;
		return;
	}

	isValid.value = true;
	try {
		await store.dispatch('fetchAddressSuggestions', {
			language,
			query,
			notFoundText: props.cannotFindAddressLabel,
			maxSuggestions: props.maxSuggestions,
		});
	} catch (err) {
		console.error(err);
	}
}

function onSearchBlur() {
	emit('onBlur', null, props.fieldName);
}

async function onSearchFocus() {
	try {
		await search(props.value);
	} catch(err) {
		console.error(err);
	}
}

async function onOptionSelected(address) {
	if (address.globalAddressKey === null) {
		try {
			await store.dispatch('resetAddress', props.value);
		} catch (err) {
			console.error(err);
		}
		return;
	}

	try {
		await store.dispatch('formatAddress', {
			language,
			address,
		});
	} catch (err) {
		console.error(err);
	}
}
function dropdownShouldOpen(VueSelect) {
	if (suggestions.value.length > 0) {
		return VueSelect.open;
	}

	return false;
}
</script>
<template>
	<div class="address-suggestions">
		<VSelect
			:options="suggestions"
			@option:selected="onOptionSelected"
			:filterable="false"
			:dropdown-should-open="dropdownShouldOpen"
			:clearSearchOnBlur="() => false"
			:clearSearchOnSelect="false"
			:placeholder="placeholder"
		>
			<template #search="{ attributes }">
				<input
					v-bind="attributes"
					:value="value"
					:id="id"
					:name="name"
					@input="onInput"
					@blur="onSearchBlur"
					@focus="onSearchFocus"
					class="vs__search"
					data-testId="postalCodeAddressLookup"
					:aria-invalid="hasError(fieldName) || null"
					:aria-errormessage="'error_' + id"
				/>
			</template>
			<template #option="{ text, globalAddressKey }">
				<address :key="globalAddressKey">
					<DsText html-element="div" text-style="bold" text-type="heading-lg">
						{{ text }}
					</DsText>
				</address>
			</template>
		</VSelect>
	</div>
</template>
<style lang="scss">
@import '../../../styles/components/c-search-dropdown';
</style>
